// The Introductions board (founder go 2026-10-20, "ok build this for the
// Admin panel"; approved mock-up "Introductions, Tighter"). Everything the
// board says and decides lives here as plain functions, so the page
// (IntroductionsBoard.jsx) only draws and the tests pin every word:
//   - one server row -> one display row (pair, journey, Waiting on, Next, Dealt)
//   - the stage strip, the tabs and the header pills
//   - the pair panel (headline block, two person cards, prompts, timeline)
//   - the URL <-> board state, the reducer (selection rules) and the keys.
//
// Copy rules (house): no dashes, never "match" about people (the old
// "Matched" status reads "Revealed"), first names only (emails only in the
// panel and in search), Eastern times with day words, sentence case.
// Colour rule (founder 2026-10-20): NO GREEN anywhere. This file names
// tones only ("purple", "amber", ...); IntroductionsBoard.css turns them into
// colours for the dark and the light theme.
//
// The words of the Waiting on column come from waitingOnLines and the Next
// column from waitingOnNextParts (compatibilityRecordUtils), the exact
// helpers behind the copy the founder approved on 2026-10-20, never retyped.
import {
  waitingOnLines,
  waitingOnNextParts,
  etWhen,
  gapLabel,
  introducedAgainText,
  earlierEndLines,
  firstWord,
  END_REASON_LABELS,
  CLOSE_REASON_LABELS,
} from "./compatibilityRecordUtils";
import { formatEt } from "../User/introPauseUtils";

// ------------------------------------------------------------------
// Fixed lists: tabs, stages, alerts, sorts, page sizes.
// ------------------------------------------------------------------

export const BOARD_TABS = [
  { key: "live", label: "Live" },
  { key: "ended", label: "Ended" },
  { key: "all", label: "All" },
  { key: "test", label: "Test pairs" },
];
const TAB_KEYS = new Set(BOARD_TABS.map((t) => t.key));
export const DEFAULT_VIEW = "live";

// The seven live stages, in journey order, each with its colour bar tone.
export const STAGES = [
  { key: "notstarted", label: "Not started", tone: "slate" },
  { key: "vault", label: "In the Vault", tone: "amberdeep" },
  { key: "reveal", label: "Waiting to reveal", tone: "pink" },
  { key: "nowords", label: "Revealed, no words yet", tone: "orchid" },
  { key: "reply", label: "Waiting on a reply", tone: "amber" },
  { key: "talking", label: "Talking", tone: "purple" },
  { key: "quiet", label: "Gone quiet", tone: "grey" },
];

// The two alert filters next to the strip (same `stage` param).
export const ALERTS = [
  { key: "tonight", label: "Due tonight", sub: "at the 6 PM run", tone: "amber" },
  { key: "ghost", label: "Ghosts and pile-ups", sub: "holding cards untouched", tone: "red" },
];
const STAGE_FILTER_KEYS = new Set([...STAGES.map((s) => s.key), ...ALERTS.map((a) => a.key)]);

// Server stage per waitingOn.kind (contract table, 2026-10-20). Used only
// when an older server sends no row.stage.
export const KIND_STAGE = {
  neither_started: "notstarted",
  vault: "vault",
  reveal: "reveal",
  reveal_confirm: "reveal",
  reveal_neither: "reveal",
  revealed_unseen: "nowords",
  hellos_only: "nowords",
  reply: "reply",
  talking: "talking",
  quiet: "quiet",
};

// `reversedLabel`: what the option reads once the order is reversed (review
// 2026-10-20: the old table's header click reached "oldest dealt first" and
// "lowest Common % first"; the board keeps that with a Reverse button).
export const SORTS = [
  { key: "attention", label: "Needs attention first", liveOnly: true },
  { key: "next", label: "Next step soonest", reversedLabel: "Next step latest", liveOnly: true },
  { key: "dealt", label: "Newest dealt", reversedLabel: "Oldest dealt" },
  { key: "activity", label: "Last activity", reversedLabel: "Longest without activity" },
  { key: "common", label: "Common %", reversedLabel: "Common %, lowest first" },
  { key: "pair", label: "Pair, A to Z", reversedLabel: "Pair, Z to A" },
];
const SORT_KEYS = new Set(SORTS.map((s) => s.key));
// An older server only sorts on its own sortBy fields.
const LEGACY_SORT_BY = { dealt: "createdAt", common: "commonAvg" };
// The default sortOrder of each sort. "attention" has none: its order is the
// server's own ladder (contract), and a sortOrder could reverse it, so it
// can never be reversed here either.
const SORT_ORDER = { next: "asc", dealt: "desc", activity: "desc", common: "desc", pair: "asc" };
const FLIP = { asc: "desc", desc: "asc" };

/** Whether this sort can be reversed (every sort but the attention ladder). */
export const canReverse = (sort) => !!SORT_ORDER[sort];

/** The order actually used: the board's own `order` when set, else the sort's default. */
export function effectiveOrder(s) {
  if (!canReverse(s.sort)) return null;
  return s.order === "asc" || s.order === "desc" ? s.order : SORT_ORDER[s.sort];
}

/** Whether the order is the reverse of this sort's default. */
export const isReversed = (s) => canReverse(s.sort) && effectiveOrder(s) !== SORT_ORDER[s.sort];

// Only a reversed order is kept: the default reads as "".
const orderFor = (sort, order) => (canReverse(sort) && FLIP[order] && order !== SORT_ORDER[sort] ? order : "");

export const PAGE_SIZES = [25, 50, 100];
export const DEFAULT_LIMIT = 25;

// Newest dealt first on every tab (founder 2026-10-21: "the Dealt dates are
// not ordered. Should be newest first"). The attention ladder is one click
// away, on the Waiting on title.
export const defaultSortFor = () => "dealt";

/** Whether this sort may be picked on this tab (attention and next are Live only). */
export function sortAllowed(view, sort, { legacyServer = false } = {}) {
  if (!SORT_KEYS.has(sort)) return false;
  const def = SORTS.find((s) => s.key === sort);
  if (def.liveOnly && view !== "live") return false;
  // An older server cannot sort by attention, next step, activity or pair,
  // and neither can a request with no view (a dashboard ?status= link): the
  // server reads `sort` only beside a view (review 2026-10-21).
  if ((legacyServer || !view) && !LEGACY_SORT_BY[sort]) return sort === defaultSortFor(view);
  return true;
}

// The column titles that sort the board (founder 2026-10-21, "Lost column
// sorting"): each names its sort. A second click on the title in use
// reverses it, as the Reverse button does.
export const HEADER_SORTS = { pair: "pair", waiting: "attention", next: "next", dealt: "dealt" };

/**
 * One column title's sort, or null when it cannot sort here (a Live-only
 * sort on another tab, or an older server). `order` is "asc" / "desc" for
 * the title in use (null for the attention ladder); `title` says what a
 * click does.
 */
export function headerSortModel(s, column, { legacyServer = false } = {}) {
  const key = HEADER_SORTS[column];
  if (!key || !sortAllowed(s.view, key, { legacyServer })) return null;
  const def = SORTS.find((x) => x.key === key);
  const active = s.sort === key;
  const order = active ? effectiveOrder(s) : null;
  const reversed = active && isReversed(s);
  const label = reversed ? def.reversedLabel : def.label;
  let title = `Sort: ${def.label}`;
  if (active) title = canReverse(key) ? `Sorted: ${label}. Click to reverse` : `Sorted: ${label}`;
  return { key, active, order, title };
}

/**
 * The sort options to show on this tab. With `reversedSort` (the current
 * sort, reversed), that option reads its reversed label.
 */
export function sortOptionsFor(view, { legacyServer = false, reversedSort = "" } = {}) {
  return SORTS.filter((s) => sortAllowed(view, s.key, { legacyServer })).map((s) =>
    s.key === reversedSort && s.reversedLabel ? { ...s, label: s.reversedLabel } : s
  );
}

// ------------------------------------------------------------------
// Small helpers.
// ------------------------------------------------------------------

const idOf = (v) => (v && typeof v === "object" ? v._id : v);
const sameId = (a, b) => a != null && b != null && String(idOf(a)) === String(idOf(b));
const msOf = (value) => {
  if (!value) return null;
  const t = new Date(value).getTime();
  return Number.isFinite(t) ? t : null;
};
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");
const statusOf = (row) => (row && (row.compatibilityStatus || row.status)) || "";

/** "Today 10:50 AM", "Mon 3:00 PM", "Tonight 6 PM" (sched), or "". */
export function whenText(value, now = Date.now(), opts) {
  const r = etWhen(value, now, opts);
  return r ? cap(r.text) : "";
}

/** The same words inside a sentence: "today 10:50 AM" stays lower case. */
const inlineWhen = (value, now, opts) => {
  const r = etWhen(value, now, opts);
  return r ? r.text : "";
};

/** "12 min", "3 h", "2 d", "1 mo": how long ago, short, for the Dealt column. "" when unknown. */
export function agoShort(value, now = Date.now()) {
  const t = msOf(value);
  const n = msOf(now) ?? Date.now();
  if (t === null || n < t) return "";
  const min = Math.floor((n - t) / 60000);
  if (min < 60) return `${Math.max(1, min)} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} h`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d} d`;
  return `${Math.max(1, Math.round(d / 30))} mo`;
}

// ------------------------------------------------------------------
// Who is who: first names only.
// ------------------------------------------------------------------

const sideUser = (row, side) => (side === "a" ? row.fromUser || row.userA : row.toUser || row.userB);

/** The member id on one side ("a" = from, "b" = to), or null. */
export function sideIdOf(row, side) {
  const u = row && sideUser(row, side);
  if (u && typeof u === "object") return u._id != null ? String(u._id) : null;
  return u != null ? String(u) : null;
}

const peopleOf = (row) => {
  const w = row && row.waitingOn;
  return w && w.people && typeof w.people === "object" ? w.people : {};
};

// A word that is (or contains) an email is never a name.
const cleanName = (s) => {
  const f = firstWord(s);
  return f && !f.includes("@") ? f : "";
};

/**
 * One side's first name: the server's fromFirst / toFirst (2026-10-20
 * contract), else the waiting-on facts' `first`, else the first word of the
 * full name. Never an email, never a surname. "No name" when nothing names
 * them (a social sign-in can come with neither).
 */
export function firstNameOf(row, side) {
  if (!row) return "No name";
  const field = side === "a" ? row.fromFirst : row.toFirst;
  const id = sideIdOf(row, side);
  const p = id ? peopleOf(row)[id] : null;
  const u = sideUser(row, side);
  return (
    cleanName(field) ||
    cleanName(p && p.first) ||
    cleanName(u && typeof u === "object" ? u.fullName : "") ||
    "No name"
  );
}

/** The first name of the row's member with this id, or "" when neither side is it. */
export function nameOfId(row, id) {
  if (!row || id == null) return "";
  if (sameId(sideIdOf(row, "a"), id)) return firstNameOf(row, "a");
  if (sameId(sideIdOf(row, "b"), id)) return firstNameOf(row, "b");
  return "";
}

// ------------------------------------------------------------------
// Live or ended, and the status words.
// ------------------------------------------------------------------

// Whether the server wrote the board's row fields on this row (2026-10-20
// contract: dueTonight is always a boolean and endedAt always present, null
// while live). An older server, or a row it could not annotate, has neither.
const annotated = (row) => typeof row.dueTonight === "boolean" && Object.prototype.hasOwnProperty.call(row, "endedAt");

/**
 * Whether the introduction is over. The server's own answer when it sent
 * one (endedAt is non-null exactly when the row is not live, the same rule
 * that splits the Live and Ended tabs). Otherwise its rule, mirrored
 * (adminWaitingOn.isLiveRow + revealBlind.revealedConnectionLive): live is
 * an open generated row, or a matched row still a live connection (chat not
 * locked, no decline, no close reason); anything else is over. Review
 * 2026-10-20: the old rule here missed closeReason and revealDeclinedAt, so
 * rows on the Ended tab read "Revealed" with the Promise actions offered.
 */
export function isEndedRow(row) {
  if (!row) return false;
  if (annotated(row)) return row.endedAt != null;
  if (row.revealClosedAt || row.unmatchedAt) return true;
  const s = statusOf(row);
  if (s === "generated") return false;
  if (s === "matched") return row.chatUnlocked === false || !!row.revealDeclinedAt || !!row.closeReason;
  return true;
}

/** Whether the honest question and Close kindly may run on it: live, real members. */
export function canActOn(row) {
  return !!row && ["generated", "matched"].includes(statusOf(row)) && !isEndedRow(row) && !row.isTestPair;
}

const isPauseClose = (row) => row && row.endReason === "paused" && (!statusOf(row) || statusOf(row) === "expired");

// Who paused: the first field that names one of the two sides (same order
// as compatibilityRecordUtils' pausedCloseLabel), by first name.
const pauserNameOf = (row) => {
  const id = [row.pausedBy, row.closedBy, row.endedBy].find((x) => x != null && nameOfId(row, x));
  return id != null ? nameOfId(row, id) : "";
};

// The status words (honest labels, founder 2026-08-31 and 2026-09-24), with
// "Matched" now "Revealed" (house rule: never "match" about people).
const STATUS_LABELS = {
  generated: "Active",
  matched: "Revealed",
  expired: "Ended before reveal",
  unmatched: "Closed after reveal",
  blocked: "Blocked",
  qa_parked: "QA parked",
};

/** "Ended before reveal", "Closed: Lily took a break", "Revealed", ... */
export function boardStatusLabel(row) {
  const s = statusOf(row);
  if (!s) return "Unknown";
  if (isPauseClose(row)) {
    const who = pauserNameOf(row);
    return who ? `Closed: ${who} took a break` : "Closed: one of them took a break";
  }
  if (s === "expired" && !row.endedBy) return "Timed out before reveal";
  if (s === "matched" && isEndedRow(row)) return "Closed after reveal";
  // A generated row the server counts as ended (revealClosedAt or
  // unmatchedAt set): never "Active" on the Ended tab.
  if (s === "generated" && isEndedRow(row)) return "Closed before reveal";
  return STATUS_LABELS[s] || cap(s.replace(/_/g, " "));
}

/**
 * How an ended introduction ended, by first name:
 * { detail: one row line, lines: the panel's lines }.
 * "Bea ended it: Not feeling it · Bea 3 of 9, Alex 9 of 9".
 */
export function endedFacts(row, now = Date.now()) {
  const lines = [];
  let detail = "";
  const s = statusOf(row);
  if (isPauseClose(row)) {
    const who = pauserNameOf(row);
    detail = row.pauseCountedAsEndAt
      ? `Counted as ${who ? `${who}'s` : "their"} own End (resumed within 24 hours)`
      : `${who || "One of them"} took a break, which closed this card`;
    lines.push(detail);
  } else if (row.endedBy && ["expired", "unmatched", "matched", "generated"].includes(s)) {
    const ender = nameOfId(row, row.endedBy) || "One of them";
    const reason = END_REASON_LABELS[row.endReason] || "";
    const verb = s === "expired" || s === "generated" ? "ended it" : "closed it";
    const who = reason ? `${ender} ${verb}: ${reason}` : `${ender} ${verb}`;
    const a = row.endAnswers;
    let counts = "";
    if (a && Number.isFinite(a.ender) && Number.isFinite(a.other)) {
      const enderSide = sameId(sideIdOf(row, "a"), row.endedBy) ? "a" : "b";
      const other = firstNameOf(row, enderSide === "a" ? "b" : "a");
      counts = `${ender} ${a.ender} of 9, ${other} ${a.other} of 9`;
    }
    detail = [who, counts].filter(Boolean).join(" · ");
    lines.push(who);
    if (counts) lines.push(`At End: ${counts}`);
  } else if (["expired", "unmatched", "matched", "generated"].includes(s)) {
    detail =
      CLOSE_REASON_LABELS[row.closeReason] ||
      END_REASON_LABELS[row.endReason] ||
      (s === "expired" ? "Timed out: no one acted" : "");
    if (detail) lines.push(detail);
  }
  const endedAt = row.endedAt || row.revealClosedAt || row.unmatchedAt || null;
  const when = inlineWhen(endedAt, now);
  if (when) {
    const gap = gapLabel(row.createdAt, endedAt);
    lines.push(`Ended ${when}${gap ? `, ${gap} after dealt` : ""}`);
  }
  return { detail, lines };
}

// ------------------------------------------------------------------
// One row.
// ------------------------------------------------------------------

// waitingOnLines' tones, in Curiouzz colours: talking is purple, never green.
const TONE_OF = { warning: "amber", success: "purple", neutral: "grey", danger: "red" };

const liveToneOf = (w, out) => {
  if (!out) return "grey";
  if (out.tone === "danger") return "red";
  // Nobody in particular is waited on yet: grey, as in the mock-up.
  if (w.kind === "neither_started") return "grey";
  // "Revealed, no words yet" is orchid (founder colour rule 2026-10-20),
  // except where one named member is waited on (amber) or both said hello (grey).
  if (w.kind === "revealed_unseen") return "orchid";
  if (w.kind === "hellos_only" && out.tone === "warning" && !/^Waiting on /.test(out.headline)) return "orchid";
  return TONE_OF[out.tone] || "grey";
};

/** The row's stage: the server's row.stage, else from waitingOn.kind, else null. */
export function stageOfRow(row) {
  if (row && typeof row.stage === "string" && row.stage) return row.stage;
  const kind = row && row.waitingOn && row.waitingOn.kind;
  return (kind && KIND_STAGE[kind]) || null;
}

const isRevealed = (row) =>
  statusOf(row) === "matched" || statusOf(row) === "unmatched" || !!row.revealCompletedAt;

// Which side the pair waits on (the "waiting" tag and the amber name).
const waitedSideOf = (row) => {
  const w = row.waitingOn;
  if (!w || isEndedRow(row) || !KIND_STAGE[w.kind]) return null;
  if (w.kind === "talking" || w.kind === "quiet" || w.kind === "neither_started") return null;
  const id = w.kind === "reveal_confirm" && w.reconfirmUserId != null ? w.reconfirmUserId : w.onUserId;
  if (id == null) return null;
  if (sameId(sideIdOf(row, "a"), id)) return "a";
  if (sameId(sideIdOf(row, "b"), id)) return "b";
  return null;
};

const TIER = {
  proven: { label: "Proven", help: "Finished a Vault or chose Reveal before", tone: "purple" },
  untested: { label: "Untested", help: "New, or not given a fair chance yet", tone: "grey" },
  ghost: { label: "Ghost", help: "Served 2+ introductions, never answered a Vault question", tone: "red" },
};
// A ghost that is a lapse (server fromUserLapsed, 2026-10-20) is proven by
// history with the last 3 introductions untouched: the never-answered
// wording would be untrue for them.
const LAPSED_HELP = "Proven before, but left the last 3 introductions untouched";

/** One member's tier: { key, label, help, tone } or null when the server sent none. */
export function tierOf(row, side) {
  const pre = side === "a" ? "fromUser" : "toUser";
  const key = row[`${pre}Tier`];
  if (!TIER[key]) return null;
  const t = { key, ...TIER[key] };
  if (key === "ghost" && row[`${pre}Lapsed`] === true) t.help = LAPSED_HELP;
  return t;
}

/** The person half of a row: name, tier dot, waited tag, pile, and the journey. */
export function personModel(row, side) {
  const id = sideIdOf(row, side);
  const pre = side === "a" ? "fromUser" : "toUser";
  const known = !!(id && peopleOf(row)[id]);
  const p = (id && peopleOf(row)[id]) || {};
  const name = firstNameOf(row, side);
  const tier = tierOf(row, side);
  const vaultRaw = Number.isFinite(p.vault) ? p.vault : row[`${pre}VaultAnswers`];
  const vault = Number.isFinite(vaultRaw) ? Math.max(0, Math.min(9, vaultRaw)) : null;
  const revealed = isRevealed(row);
  const w = row.waitingOn || {};

  // Reveal dot: pink filled = tapped; outlined = could tap (9 of 9), not yet.
  let tap = "none";
  const tapped =
    revealed ||
    !!p.tapAt ||
    (w.tap && sameId(w.tap.byId, id)) ||
    (w.kind === "reveal_confirm" && known);
  if (tapped) tap = "yes";
  else if (vault === 9) tap = "no";

  // Hello dot: purple filled = said hello after the reveal.
  let hello = "none";
  if (revealed && known) hello = p.helloSent && !p.helloGone ? "yes" : "no";

  const wrote = revealed && known && Number.isFinite(p.ownWords) ? p.ownWords : null;
  const untouched = Number(row[`${pre}Untouched`]) || 0;

  return {
    id,
    name,
    tier,
    pile: untouched > 0 ? untouched : 0,
    vault,
    segs: Array.from({ length: 9 }, (_, i) =>
      vault !== null && i < vault ? (vault === 9 ? "full" : "part") : "empty"
    ),
    vaultTitle: vault === null ? `${name}: Vault count not sent` : `${name}: ${vault} of 9 Vault answers`,
    tap,
    tapTitle: tap === "yes" ? `${name} tapped Reveal` : tap === "no" ? `${name} has not tapped Reveal` : `${name} cannot tap Reveal yet`,
    hello,
    helloTitle:
      hello === "yes" ? `${name} said hello` : hello === "no" ? `${name} has not said hello` : "Not revealed yet",
    wrote,
    wroteText: wrote === null ? "" : String(wrote),
    wroteTitle: wrote === null ? "Not revealed yet" : `${name} wrote ${wrote} since the hello`,
  };
}

// What the Next column says when the server named no next step.
const DUE_WHAT = {
  prompt: "Reminder",
  close: "Closes if still quiet",
  expire: "Closes if nobody answers",
  quiet_card_close: "Quiet card: closes if not opened",
};

// Whether both people are in a revealed conversation: the server calls it
// talking or quiet (both wrote words of their own), or each of the two has a
// live reveal hello or words of their own (personModel's hello and wrote
// facts). Since 2026-10-21 (backend 18918fd, founder: "Stop the system from
// closing them where both people wrote") the Promise sweep never closes such
// a pair: it reads raw counts, hellos included, so two live hellos count as
// both wrote there too.
const bothIn = (row, w) => {
  if (!isRevealed(row)) return false;
  const stage = stageOfRow(row);
  if (stage === "talking" || stage === "quiet" || w.kind === "talking" || w.kind === "quiet") return true;
  return ["a", "b"].every((side) => {
    const id = sideIdOf(row, side);
    const p = id ? peopleOf(row)[id] : null;
    if (!p) return false;
    const hello = !!p.helloSent && !p.helloGone;
    const words = Number.isFinite(p.ownWords) && p.ownWords > 0;
    return hello || words;
  });
};

function liveNext(row, w, now, nextRunAt) {
  const parts = waitingOnNextParts(row, now);
  if (parts) {
    const atMs = msOf(parts.at);
    const runMs = msOf(nextRunAt);
    const urgent = !!row.dueTonight || (atMs !== null && runMs !== null && atMs <= runMs);
    return { when: cap(parts.when), what: parts.what, tone: urgent ? "urgent" : "normal", urgent };
  }
  if (row.dueTonight) {
    return {
      when: whenText(nextRunAt, now, { sched: true }) || "Tonight",
      what: DUE_WHAT[row.dueTonightWhat] || "",
      tone: "urgent",
      urgent: true,
    };
  }
  const muted = (when, what) => ({ when, what, tone: "muted", urgent: false });
  if (!w) return muted("", "");
  if (w.blockedBy === "promise_off") return muted("Nothing scheduled", "The Promise is off");
  if (w.blockedBy === "test_pair" || row.isTestPair) return muted("Held", "Test pair");
  if (w.blockedBy) return muted("Held", "The Promise will not close this");
  // A revealed conversation both people are in: nothing closes it now but
  // one of them (founder-approved words, 2026-10-21: one line, "Change
  // Nothing Due to Stays open, until one of them closes it").
  if (bothIn(row, w)) return muted("Stays open, until one of them closes it", "");
  return muted("Nothing due", "");
}

/**
 * One server row as the board draws it. `now` (tests pin it) and
 * `nextRunAt` (response.promise.nextRunAt: what counts as tonight's run).
 */
export function boardRowModel(row, { now = Date.now(), nextRunAt = null } = {}) {
  const a = personModel(row, "a");
  const b = personModel(row, "b");
  const ended = isEndedRow(row);
  const waited = ended ? null : waitedSideOf(row);
  if (waited) (waited === "a" ? a : b).waited = true;
  const againText = introducedAgainText(row);

  let tone = "grey";
  let headline = "";
  let detail = "";
  let next;
  const w = row.waitingOn;
  if (ended) {
    tone = "ended";
    headline = boardStatusLabel(row);
    detail = endedFacts(row, now).detail;
    const endedAt = row.endedAt || row.revealClosedAt || row.unmatchedAt || null;
    const when = inlineWhen(endedAt, now);
    const gap = gapLabel(row.createdAt, endedAt);
    next = { when: when ? `Ended ${when}` : "Ended", what: gap ? `${gap} after dealt` : "", tone: "ended", urgent: false };
  } else {
    const out = waitingOnLines(row, now);
    if (out && out.headline) {
      tone = liveToneOf(w, out);
      headline = out.headline;
      detail = out.lines[0] || out.chips.map((c) => c.text).join(", ");
    } else {
      // An older server, or a row the server could not read facts for.
      headline = boardStatusLabel(row);
    }
    next = liveNext(row, out ? w : null, now, nextRunAt);
  }

  const common = Number.isFinite(row.commonAvg) ? `${Math.round(row.commonAvg)}%` : "";
  return {
    id: String(row._id),
    label: `${a.name} and ${b.name}`,
    a,
    b,
    ended,
    test: !!row.isTestPair,
    again: !!againText,
    againText: againText || "",
    stage: ended ? null : stageOfRow(row),
    dueTonight: !!row.dueTonight,
    ghostFlag: !!row.ghostFlag,
    tone,
    headline,
    detail,
    next,
    dealt: {
      when: whenText(row.createdAt, now),
      title: row.createdAt ? `Dealt ${formatEt(row.createdAt)}` : "",
      ago: agoShort(row.createdAt, now),
      common,
    },
    actionable: canActOn(row),
  };
}

// ------------------------------------------------------------------
// The pair panel.
// ------------------------------------------------------------------

function personCard(row, side, now) {
  const m = personModel(row, side);
  const id = m.id;
  const pre = side === "a" ? "fromUser" : "toUser";
  const known = !!(id && peopleOf(row)[id]);
  const p = (id && peopleOf(row)[id]) || {};
  const u = sideUser(row, side);
  const revealed = isRevealed(row);
  const lastIn = inlineWhen(p.lastInApp, now);
  let stageSeen = "";
  if (revealed) {
    const saw = inlineWhen(p.sawRevealAt, now);
    stageSeen = saw ? `Saw the reveal ${saw}` : known ? "Has not seen the reveal yet" : "";
  } else {
    const seen = inlineWhen(row[`${pre}SeenAt`], now);
    stageSeen = seen ? `Stage showed it ${seen}` : `Never surfaced on ${m.name}'s stage`;
  }
  return {
    id,
    name: m.name,
    tier: m.tier,
    email: (u && typeof u === "object" && u.email) || "",
    vaultText:
      m.vault === null
        ? m.tap === "yes"
          ? "Tapped Reveal"
          : "Vault count not sent"
        : `Vault ${m.vault} of 9${m.tap === "yes" ? " · tapped Reveal" : ""}`,
    inApp: lastIn ? `In the app ${lastIn}` : "",
    stageSeen,
    untouched: m.pile ? `Holds ${m.pile} untouched introduction${m.pile === 1 ? "" : "s"}` : "",
    appVersion: p.appVersion ? `App ${p.appVersion}` : "",
    onBreak: p.onBreak === true ? "On a break" : "",
    memberHref: id ? `/user/view-user/${id}` : null,
  };
}

/**
 * Everything the right-side panel shows for one row, except the timeline
 * (its own request): label, sub, headline block, next step, two person
 * cards, Promise prompt history, and whether the Promise actions apply.
 */
export function boardPanelModel(row, { now = Date.now(), nextRunAt = null } = {}) {
  const m = boardRowModel(row, { now, nextRunAt });
  // Earlier Ends name the ender by first name, like every other panel line
  // (the helper's default is the email; review 2026-10-20).
  const earlier = earlierEndLines(row, (code) => END_REASON_LABELS[code], nameOfId);
  let lines;
  if (m.ended) {
    lines = endedFacts(row, now).lines.concat(earlier);
  } else {
    const out = waitingOnLines(row, now);
    lines = out ? [...out.chips.map((c) => c.text), ...out.lines] : [];
    lines = lines.concat(earlier);
  }
  const prompts = (Array.isArray(row.promisePrompts) ? row.promisePrompts : [])
    .filter((x) => x && x.at)
    .map((x) => ({ who: nameOfId(row, x.userId) || "One of them", when: whenText(x.at, now) }));
  const dealtInline = inlineWhen(row.createdAt, now);
  const sub = [
    dealtInline ? `Dealt ${dealtInline}` : "",
    m.againText ? m.againText.charAt(0).toLowerCase() + m.againText.slice(1) : "",
    m.dealt.common ? `${m.dealt.common} in common` : "",
    m.test ? "test pair" : "",
  ]
    .filter(Boolean)
    .join(" · ");
  return {
    id: m.id,
    label: m.label,
    sub,
    tone: m.tone,
    headline: m.headline,
    lines,
    next: !m.ended && m.next && m.next.when ? m.next : null,
    people: [personCard(row, "a", now), personCard(row, "b", now)],
    prompts,
    actionable: m.actionable,
    ended: m.ended,
  };
}

/** Timeline events from GET /admin/interactions/:id/timeline as lines: { when, text, future }. */
export function timelineModel(events, now = Date.now()) {
  return (Array.isArray(events) ? events : [])
    .filter((e) => e && typeof e.label === "string" && e.label.trim())
    .map((e) => ({
      when: e.at ? whenText(e.at, now, { sched: !!e.future }) : "",
      text: e.label.trim(),
      future: !!e.future,
    }));
}

// ------------------------------------------------------------------
// The strip, the tabs, the header pills, empty words, bulk summaries.
// ------------------------------------------------------------------

const countOf = (obj, key) => (obj && typeof obj === "object" && Number.isFinite(obj[key]) ? obj[key] : null);

/**
 * The stage strip: seven proportional stage buttons and two alerts. With no
 * stageCounts (an older server) the counts hide and every stage is the same width.
 */
export function stageStripModel(stageCounts, activeStage, { off = "", nextRunAt = null, now = Date.now() } = {}) {
  const hasCounts = !off && !!stageCounts && typeof stageCounts === "object";
  // `off` (stripOffText): the server cannot filter by stage, so nothing
  // reads as pressed and the buttons are disabled.
  const on = (key) => !off && activeStage === key;
  return {
    hasCounts,
    off,
    stages: STAGES.map((st) => {
      const count = hasCounts ? countOf(stageCounts, st.key) : null;
      return { ...st, count, grow: Math.max(1, count || 0), on: on(st.key) };
    }),
    alerts: ALERTS.map((al) => ({
      ...al,
      ...(al.key === "tonight" ? dueRunWords(nextRunAt, now) : {}),
      count: hasCounts ? countOf(stageCounts, al.key) : null,
      on: on(al.key),
    })),
  };
}

// "Due tonight" is true only before the day's 6 PM run (founder board,
// 2026-10-20): after it, the server's "due" rows are due at TOMORROW's run,
// so the button names the run they are really due at. The day is Eastern.
const etDay = (ms) => new Intl.DateTimeFormat("en-CA", { timeZone: "America/Toronto" }).format(new Date(ms));
export function dueRunWords(nextRunAt, now = Date.now()) {
  const w = nextRunAt ? etWhen(nextRunAt, now, { sched: true }) : null;
  if (!w) return {};
  const runMs = new Date(nextRunAt).getTime();
  const nowMs = new Date(now).getTime();
  const sub = `at the ${w.clock} run`;
  if (etDay(runMs) === etDay(nowMs)) return { label: "Due tonight", sub };
  if (etDay(runMs) === etDay(nowMs + 24 * 60 * 60 * 1000)) return { label: "Due tomorrow", sub };
  return { label: `Due ${w.text.split(" ")[0]}`, sub };
}

// Phone width: the panel is a full-screen sheet there (IntroductionsBoard.css).
const PHONE_QUERY = "(max-width: 767.98px)";

/** Whether the window is phone width (the panel then acts as a modal sheet). */
export function isPhoneWidth(win = typeof window !== "undefined" ? window : null) {
  const mm = win && win.matchMedia;
  return typeof mm === "function" ? !!mm.call(win, PHONE_QUERY).matches : false;
}

/** The four tabs with tabCounts (counts hide when the server sent none). */
export function tabsModel(tabCounts, view) {
  return BOARD_TABS.map((t) => ({ ...t, count: countOf(tabCounts, t.key), on: view === t.key }));
}

/** The header pills: dealt in 24 h, inventory rules, the Promise. */
export function headerPills({ dealing, promise } = {}, now = Date.now()) {
  const out = [];
  if (dealing && Number.isFinite(dealing.dealtLast24h)) {
    out.push({
      key: "dealt",
      tone: "plain",
      before: "Dealt in 24 h",
      strong: String(dealing.dealtLast24h),
      after: dealing.redealtLast24h > 0 ? `(${dealing.redealtLast24h} again)` : "",
      title: "Real members only: a pair with a test account never counts. Again = these two had been dealt to each other before.",
    });
  }
  if (dealing && typeof dealing.inventoryGateOn === "boolean") {
    out.push({
      key: "inventory",
      tone: dealing.inventoryGateOn ? "purple" : "grey",
      text: `Inventory rules ${dealing.inventoryGateOn ? "on" : "off"}`,
      title: "The dealing gate that holds new introductions back from members with untouched ones.",
    });
  }
  if (promise && typeof promise.on === "boolean") {
    const next = promise.on ? etWhen(promise.nextRunAt, now, { sched: true }) : null;
    out.push({
      key: "promise",
      tone: promise.on ? "blue" : "grey",
      text: promise.on ? `Promise on${next ? ` · next run ${next.text}` : ""}` : "Promise off",
      title: "The Curiouzz Promise: reminders and kind closes run at 6 PM Eastern.",
    });
  }
  return out;
}

/** The empty state's words for this tab, stage and search. */
export function emptyStateText(state) {
  if (state.search && state.search.trim()) return "No introductions here. Try another stage or clear the search.";
  if (state.view === "live" && state.stage) {
    const def = [...STAGES, ...ALERTS].find((x) => x.key === state.stage);
    return `Nothing in "${def ? def.label : state.stage}" right now. Click it again to see every live introduction.`;
  }
  switch (state.view) {
    case "live":
      return "No live introductions right now.";
    case "ended":
      return "No ended introductions for these dates.";
    case "test":
      return "No test pairs here.";
    case "all":
      return "No introductions for these dates.";
    default:
      return "No introductions with this filter. Remove the filter to see every live one.";
  }
}

/**
 * The result of a bulk Promise action as one line, from each pair's
 * server message: "3 pairs: The honest question was sent. 1 pair: Nothing to do: ...".
 */
export function bulkResultText(results) {
  const counts = new Map();
  (results || []).forEach((r) => {
    const msg = (r && r.message) || (r && r.ok ? "Done." : "The action did not complete.");
    counts.set(msg, (counts.get(msg) || 0) + 1);
  });
  return [...counts.entries()]
    .map(([msg, n]) => `${n} pair${n === 1 ? "" : "s"}: ${msg}`)
    .join(" ");
}

// ------------------------------------------------------------------
// URL <-> state, the request, the reducer, the keys.
// ------------------------------------------------------------------

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const OBJECT_ID_RE = /^[a-f\d]{24}$/i;

/**
 * The board's filters from the query string. A legacy ?status=… link (the
 * dashboard tile's ?status=revealed) with no view keeps view null: the
 * status goes to the server unchanged, as before, and shows as a chip.
 */
export function parseBoardQuery(search) {
  const q = new URLSearchParams(search || "");
  const status = (q.get("status") || "").trim();
  const memberRaw = q.get("member") || "";
  const member = OBJECT_ID_RE.test(memberRaw) ? memberRaw : "";
  let view = q.get("view");
  if (!TAB_KEYS.has(view)) view = status ? null : member ? "all" : DEFAULT_VIEW;
  const stageRaw = q.get("stage") || "";
  const stage = view === "live" && STAGE_FILTER_KEYS.has(stageRaw) ? stageRaw : "";
  const sortRaw = q.get("sort") || "";
  const sort = sortAllowed(view, sortRaw) ? sortRaw : defaultSortFor(view);
  const order = orderFor(sort, q.get("order") || "");
  const page = Math.max(1, parseInt(q.get("page"), 10) || 1);
  const limitN = parseInt(q.get("limit"), 10);
  const limit = PAGE_SIZES.includes(limitN) ? limitN : DEFAULT_LIMIT;
  const date = (v) => (DATE_RE.test(v || "") ? v : "");
  return {
    view,
    stage,
    search: q.get("search") || "",
    sort,
    order,
    page,
    limit,
    from: date(q.get("from")),
    to: date(q.get("to")),
    status,
    member,
  };
}

/** The query string for these filters (defaults left out), without the "?". */
export function buildBoardQuery(s) {
  const q = new URLSearchParams();
  if (s.view) q.set("view", s.view);
  if (s.status) q.set("status", s.status);
  if (s.member) q.set("member", s.member);
  if (s.stage) q.set("stage", s.stage);
  if (s.search) q.set("search", s.search);
  if (s.sort && s.sort !== defaultSortFor(s.view)) q.set("sort", s.sort);
  const order = orderFor(s.sort, s.order);
  if (order) q.set("order", order);
  if (s.page > 1) q.set("page", String(s.page));
  if (s.limit && s.limit !== DEFAULT_LIMIT) q.set("limit", String(s.limit));
  if (s.from) q.set("from", s.from);
  if (s.to) q.set("to", s.to);
  return q.toString();
}

/**
 * GET /admin/interactions params for these filters. `search` is the
 * debounced text. `legacyServer` (a response to a request that sent `view`
 * had no tabCounts, so the server predates the 2026-10-20 contract) maps
 * the tabs onto today's status and testOnly params, and the member onto
 * fromUser (one side only).
 *
 * The legacy params still carry `view` (review 2026-10-20): an older server
 * ignores it, and a server updated while the board is open answers with
 * tabCounts, so the board leaves legacy mode on its own. A ?status= link
 * (view null) sends no view, and its answer decides nothing (see
 * legacyVerdict).
 */
export function boardRequestParams(s, { search, legacyServer = false } = {}) {
  const text = String(search !== undefined ? search : s.search || "").trim();
  const p = { page: s.page, limit: s.limit };
  if (text) p.search = text;
  if (s.from) {
    p.startDate = s.from;
    p.fromDate = s.from;
  }
  if (s.to) {
    p.endDate = s.to;
    p.toDate = s.to;
  }
  if (s.status) p.status = s.status;
  if (s.view === "test") p.testOnly = 1;
  if (legacyServer) {
    if (s.view) p.view = s.view;
    p.sortBy = LEGACY_SORT_BY[s.sort] || "createdAt";
    p.sortOrder = LEGACY_SORT_BY[s.sort] ? effectiveOrder(s) : "desc";
    if (!s.status && s.view === "live") p.status = "generated,matched";
    if (!s.status && s.view === "ended") p.status = "expired,unmatched,blocked";
    if (s.member) p.fromUser = s.member;
    return p;
  }
  if (s.view) p.view = s.view;
  p.sort = s.sort;
  const order = effectiveOrder(s);
  if (order) p.sortOrder = order;
  if (LEGACY_SORT_BY[s.sort]) p.sortBy = LEGACY_SORT_BY[s.sort];
  if (s.stage && s.view === "live") p.stage = s.stage;
  if (s.member) p.member = s.member;
  return p;
}

/**
 * What one response says about the server, or null when it says nothing.
 * Only a request that sent `view` can tell: the new server adds tabCounts
 * only then (contract: "when view is absent, behave exactly as today").
 * Review 2026-10-20: reading a ?status= link's answer as "older server"
 * locked the board into legacy mode on the new server for the whole visit.
 * true = older server, false = the 2026-10-20 server.
 */
export function legacyVerdict(params, body) {
  if (!params || !params.view) return null;
  return !(body && Object.prototype.hasOwnProperty.call(body, "tabCounts"));
}

/**
 * Why the stage strip cannot filter right now, or "" when it can. An older
 * server ignores `stage`; over the live cap the server reads one page of
 * rows, so it neither counts nor filters by stage (review 2026-10-20: the
 * strip showed a pressed stage over the unfiltered list).
 */
export function stripOffText({ legacyServer = false, liveCapped = false } = {}) {
  if (legacyServer) return "Stage filters need the updated server.";
  if (liveCapped) {
    return "More live introductions than the board reads at once. Stage counts and stage filters are off, and the attention and next step sorts fall back to dealt date.";
  }
  return "";
}

// The old page's status codes in the board's words (review 2026-10-20: a
// saved ?status=matched link printed the raw code). "revealed" is the
// dashboard tile's virtual filter (ever revealed), shown as the contract
// names it.
const STATUS_CHIP_WORDS = { ...STATUS_LABELS, revealed: "revealed" };

/** The legacy status chip: "Filtered: revealed", "Filtered: Active or Revealed", or "Filtered by status". */
export function statusChipText(status) {
  const parts = String(status || "")
    .split(",")
    .map((x) => x.trim().toLowerCase())
    .filter(Boolean);
  if (!parts.length || parts.some((x) => !STATUS_CHIP_WORDS[x])) return "Filtered by status";
  return `Filtered: ${parts.map((x) => STATUS_CHIP_WORDS[x]).join(" or ")}`;
}

/** The board's starting state from the URL: filters plus no selection, no panel. */
export function initialBoardState(search) {
  return { ...parseBoardQuery(search), selected: [], openId: null, cursor: -1 };
}

/** The Reverse button's state: hidden for the attention ladder. */
export function reverseModel(s) {
  if (!canReverse(s.sort)) return null;
  const on = isReversed(s);
  return { on, label: on ? "Back to the usual order" : "Reverse the order" };
}

// Any filter, page or tab change: back to page 1 (unless a page was
// asked for), and the selection is EMPTIED. The old table kept ticked rows
// across pages and filters, so "Delete All" could delete rows nobody could
// see (founder's inventory, 2026-10-20): selection only ever covers the
// rows on screen.
// A reversed order belongs to its sort: a new sort starts in its usual order.
const filterChange = (s, patch) => {
  const next = { ...s, ...patch };
  const keptOrder = next.sort === s.sort ? s.order : "";
  return {
    ...next,
    order: orderFor(next.sort, patch.order !== undefined ? patch.order : keptOrder),
    page: patch.page !== undefined ? patch.page : 1,
    selected: [],
    cursor: -1,
  };
};

/** The board's reducer: filters, selection, panel and the J/K cursor. */
export function boardReducer(s, a) {
  switch (a.type) {
    case "view": {
      if (!TAB_KEYS.has(a.view)) return s;
      // Another tab opens in its usual order: a reversed Dealt stays behind
      // (review 2026-10-21, else Live could open oldest first).
      const keep = sortAllowed(a.view, s.sort) && s.view === a.view;
      return filterChange(s, { view: a.view, stage: "", status: "", sort: keep ? s.sort : defaultSortFor(a.view), order: keep ? s.order : "" });
    }
    case "stage": {
      if (!STAGE_FILTER_KEYS.has(a.stage)) return s;
      const toLive = s.view !== "live";
      return filterChange(s, {
        view: "live",
        status: "",
        stage: s.stage === a.stage && !toLive ? "" : a.stage,
        sort: toLive ? defaultSortFor("live") : s.sort,
        order: toLive ? "" : s.order,
      });
    }
    case "search":
      return filterChange(s, { search: a.search || "" });
    case "sort":
      return sortAllowed(s.view, a.sort) ? filterChange(s, { sort: a.sort }) : s;
    case "reverse": {
      const order = effectiveOrder(s);
      return order ? filterChange(s, { order: FLIP[order] }) : s;
    }
    case "headerSort":
      // The title in use reverses its sort (the attention ladder stays put).
      return boardReducer(s, a.sort === s.sort ? { type: "reverse" } : { type: "sort", sort: a.sort });
    case "clearStage":
      // The strip cannot filter here (older server, live cap): drop the stage.
      return s.stage ? filterChange(s, { stage: "" }) : s;
    case "limit":
      return PAGE_SIZES.includes(a.limit) ? filterChange(s, { limit: a.limit }) : s;
    case "dates":
      return filterChange(s, {
        from: DATE_RE.test(a.from || "") ? a.from : "",
        to: DATE_RE.test(a.to || "") ? a.to : "",
      });
    case "clearStatus": {
      const view = s.view || DEFAULT_VIEW;
      return filterChange(s, { status: "", view, sort: s.view ? s.sort : defaultSortFor(view), order: s.view ? s.order : "" });
    }
    case "member":
      if (!OBJECT_ID_RE.test(a.member || "")) return s;
      return filterChange(s, {
        member: a.member,
        view: "all",
        stage: "",
        status: "",
        sort: sortAllowed("all", s.sort) ? s.sort : defaultSortFor("all"),
      });
    case "clearMember":
      return filterChange(s, { member: "" });
    case "page":
      return filterChange(s, { page: Math.max(1, Math.floor(Number(a.page)) || 1) });
    case "replace": {
      // Back / forward, or a link inside the app: the URL wins.
      const next = parseBoardQuery(a.search);
      if (buildBoardQuery(next) === buildBoardQuery(s)) return s;
      return { ...next, selected: [], openId: s.openId, cursor: -1 };
    }
    case "toggleRow": {
      const visible = a.visibleIds || [];
      if (!visible.includes(a.id)) return s;
      const on = s.selected.includes(a.id);
      return { ...s, selected: on ? s.selected.filter((x) => x !== a.id) : visible.filter((x) => x === a.id || s.selected.includes(x)) };
    }
    case "toggleAll": {
      const visible = a.visibleIds || [];
      const all = visible.length > 0 && visible.every((x) => s.selected.includes(x));
      return { ...s, selected: all ? [] : [...visible] };
    }
    case "clearSelection":
      return s.selected.length ? { ...s, selected: [] } : s;
    case "rowsLoaded": {
      // A refresh keeps only ticks on rows still on screen.
      const ids = a.ids || [];
      return {
        ...s,
        selected: s.selected.filter((x) => ids.includes(x)),
        cursor: s.cursor >= ids.length ? ids.length - 1 : s.cursor,
      };
    }
    case "open":
      return { ...s, openId: a.id || null, cursor: Number.isInteger(a.index) ? a.index : s.cursor };
    case "close":
      return s.openId ? { ...s, openId: null } : s;
    case "move": {
      const ids = a.ids || [];
      if (!ids.length) return s;
      const start = s.cursor < 0 ? (a.delta > 0 ? -1 : ids.length) : s.cursor;
      const cursor = Math.max(0, Math.min(ids.length - 1, start + a.delta));
      // With the panel open, J / K walk the panel along.
      return { ...s, cursor, openId: s.openId ? ids[cursor] : s.openId };
    }
    case "openCursor": {
      const ids = a.ids || [];
      if (s.cursor < 0 || s.cursor >= ids.length) return s;
      return { ...s, openId: ids[s.cursor] };
    }
    default:
      return s;
  }
}

const TYPING_INPUT_TYPES = new Set(["checkbox", "radio", "button", "submit", "reset", "range", "color", "file"]);

/** Whether a key event's target is somewhere a person types (the board ignores keys there). */
export function isTypingTarget(t) {
  if (!t || !t.tagName) return false;
  if (t.isContentEditable) return true;
  const tag = String(t.tagName).toUpperCase();
  if (tag === "TEXTAREA" || tag === "SELECT") return true;
  if (tag === "INPUT") return !TYPING_INPUT_TYPES.has(String(t.type || "text").toLowerCase());
  return false;
}

/**
 * The board action for one keydown, or null: J / K move, Enter opens,
 * / searches, Esc closes the panel. Nothing while typing in an input, with
 * a modifier held, or for Enter on a control that has its own Enter.
 */
export function boardKeyAction(e) {
  if (!e || e.defaultPrevented) return null;
  if (e.metaKey || e.ctrlKey || e.altKey) return null;
  if (isTypingTarget(e.target)) return null;
  switch (e.key) {
    case "j":
    case "J":
      return "down";
    case "k":
    case "K":
      return "up";
    case "/":
      return "search";
    case "Escape":
      return "close";
    case "Enter": {
      const tag = e.target && e.target.tagName ? String(e.target.tagName).toUpperCase() : "";
      if (["BUTTON", "A", "SUMMARY", "INPUT"].includes(tag)) return null;
      return "open";
    }
    default:
      return null;
  }
}
